import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useNotify, useDataProvider } from 'react-admin';
import { DragDropFileUpload } from './DragDropFileUpload';

interface EquipmentFile {
  id?: number;
  fileId: number;
  file?: {
    id: number;
    name: string;
    url?: string;
    mimeType?: string;
    size?: number;
  };
}

interface EquipmentFileUploadProps {
  entityType: 'maintenance' | 'fuel' | 'inspection' | 'incident' | 'assignment';
  entityId?: number; // For existing records
  files?: EquipmentFile[];
  onFilesChange?: (files: EquipmentFile[]) => void;
  disabled?: boolean;
  title?: string;
}

export const EquipmentFileUpload: React.FC<EquipmentFileUploadProps> = ({
  entityType,
  entityId,
  files = [],
  onFilesChange,
  disabled = false,
  title = 'File Attachments',
}) => {
  const [localFiles, setLocalFiles] = useState<EquipmentFile[]>(files);
  const [isLoading, setIsLoading] = useState(false);
  
  const notify = useNotify();
  const dataProvider = useDataProvider();

  useEffect(() => {
    setLocalFiles(files);
  }, [files]);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const handleFileUploadSuccess = async (uploadedFile: any) => {
    const newFile: EquipmentFile = {
      fileId: uploadedFile.id,
      file: uploadedFile,
    };

    // If we have an entityId, save to backend immediately
    if (entityId) {
      setIsLoading(true);
      try {
        const result = await dataProvider.create(`equipment/files/${entityType}/${entityId}`, {
          data: {
            fileId: newFile.fileId,
          },
        });
        
        // Add the file with the ID from the backend
        const savedFile = { ...newFile, id: result.data.id };
        const updatedFiles = [...localFiles, savedFile];
        setLocalFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
        
        notify('File attached successfully', { type: 'success' });
      } catch (error) {
        notify('Failed to attach file', { type: 'error' });
        console.error('Error attaching file:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // For new records, just add to the list
      const updatedFiles = [...localFiles, newFile];
      setLocalFiles(updatedFiles);
      onFilesChange?.(updatedFiles);
    }
  };

  const handleRemoveFile = async (index: number) => {
    const fileToRemove = localFiles[index];
    
    // If we have an entityId and the file has an ID, delete from backend
    if (entityId && fileToRemove.id) {
      setIsLoading(true);
      try {
        await dataProvider.delete(
          `equipment/files/${entityType}/${entityId}/${fileToRemove.fileId}`,
          { id: fileToRemove.fileId }
        );
        
        const updatedFiles = localFiles.filter((_, i) => i !== index);
        setLocalFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
        
        notify('File removed successfully', { type: 'success' });
      } catch (error) {
        notify('Failed to remove file', { type: 'error' });
        console.error('Error removing file:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // For new records, just remove from the list
      const updatedFiles = localFiles.filter((_, i) => i !== index);
      setLocalFiles(updatedFiles);
      onFilesChange?.(updatedFiles);
    }
  };

  const handleDownloadFile = (file: EquipmentFile) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" color="primary">
          {title}
        </Typography>
      </Box>

      {localFiles.length === 0 ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          No files attached. Drag & drop or click below to upload files.
        </Alert>
      ) : (
        <List>
          {localFiles.map((file, index) => (
            <ListItem
              key={index}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:last-child': { mb: 0 },
              }}
            >
              <ListItemIcon>
                <AttachFileIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.file?.name || 'Unnamed file'}
                secondary={formatFileSize(file.file?.size)}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="download"
                  onClick={() => handleDownloadFile(file)}
                  disabled={!file.file?.url}
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleRemoveFile(index)}
                  disabled={disabled || isLoading}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Box sx={{ mt: 2 }}>
        <DragDropFileUpload
          onUploadSuccess={handleFileUploadSuccess}
          folderPath={`equipment/${entityType}`}
          description={`${entityType} file`}
          acceptTypes="image/*,.pdf,.doc,.docx"
          maxSizeMB={10}
          title="Upload File"
          subtitle="Drag & drop files here or click to select"
          buttonText="Select Files"
          disabled={isLoading || disabled}
        />
      </Box>
    </Paper>
  );
};